import type { AisleCoverage, KitEvent, MapFrameTransform, TrailPoint } from "../api/rest";
import { applyTransform, type Point } from "./mapCalibration";

/**
 * The run's walk and its timeline, as pure data (ADR 259). Both are read from the
 * telemetry planes slice B landed; nothing here talks to the network or the DOM.
 */

/** A run of consecutive poses that may be drawn as one polyline. */
export interface TrailSegment {
  points: Point[];
  /** False when the kit did not trust its own pose along this stretch. */
  localized: boolean;
}

/**
 * Poses → drawable segments, in the warehouse frame.
 *
 * Two things break a segment, and both matter:
 *
 * - **A gap.** The edge records at native rate only while localized AND moving,
 *   one heartbeat pose otherwise, so a hole in the stream means "off or
 *   unlocalized" — never "parked" (that still emits). Joining across it would
 *   draw a straight line down a path the kit never walked, which is exactly the
 *   kind of invented evidence this panel exists to avoid.
 * - **A change in trust.** An unlocalized stretch is a guess and must be drawn
 *   as one, so it gets its own segment rather than being blended into a
 *   confident line.
 *
 * `gapMs` defaults to 4 buckets: wide enough that ordinary jitter does not
 * shred the line, narrow enough that a real dropout still shows as a break.
 */
export function trailSegments(
  points: readonly TrailPoint[],
  transform: MapFrameTransform,
  bucketMs: number,
  gapFactor = 4,
): TrailSegment[] {
  const gapMs = Math.max(1, bucketMs) * gapFactor;
  const out: TrailSegment[] = [];
  let current: TrailSegment | null = null;
  let prevTs = 0;
  for (const p of points) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    const localized = p.confidence > 0;
    const broken = current === null || localized !== current.localized || p.bucket_ts - prevTs > gapMs;
    if (broken) {
      // Carry the previous point into the new segment so a trust change or a
      // short gap stays a continuous line, not two lines with a hole between.
      const prev: TrailSegment | null = current;
      const seed: Point[] = prev && prev.points.length > 0 && p.bucket_ts - prevTs <= gapMs
        ? [prev.points[prev.points.length - 1]]
        : [];
      current = { points: seed, localized };
      out.push(current);
    }
    current!.points.push(applyTransform(transform, p.x, p.y));
    prevTs = p.bucket_ts;
  }
  return out.filter((s) => s.points.length > 1);
}

/** Total walked distance, meters — the honest headline for a trail panel. */
export function trailLengthM(segments: readonly TrailSegment[]): number {
  let total = 0;
  for (const seg of segments) {
    for (let i = 1; i < seg.points.length; i++) {
      total += Math.hypot(seg.points[i][0] - seg.points[i - 1][0],
                          seg.points[i][1] - seg.points[i - 1][1]);
    }
  }
  return total;
}

export type TimelineKind = "event" | "aisle_start" | "aisle_done";

/**
 * Every entry is a real instant.
 *
 * That was not always true: an aisle milestone comes from `scan_ts`, which used
 * to hold the iC3 export's naive wall clock pinned to UTC, so the two halves of
 * this list were stamped by clocks that meant different things and ADR 262 had
 * to carry a discriminator to tell them apart on render. ADR 265 fixed the
 * storage — the parser now reads that wall clock on the site's own zone — so
 * both halves name the same kind of moment and one formatter serves both. The
 * zone to render them in is the warehouse's, and it is the caller's to supply.
 */
export interface TimelineEntry {
  ts: number;
  type: TimelineKind;
  /** Event vocabulary value for `event`, aisle key for the aisle marks. */
  label: string;
  /** Severity-ish class the UI maps to a token colour; never colour by itself. */
  tone: "neutral" | "good" | "warn";
}

/** Kit events worth a line on an operator's timeline, in the vocabulary slice B
 *  appended to `device_events`. Anything else (battery samples, acks, motion
 *  chatter) is real but not a milestone, and would bury the four that are. */
const EVENT_TONE: Record<string, TimelineEntry["tone"]> = {
  inventory_start: "good",
  inventory_finish: "good",
  inventory_stop: "warn",
  localization_lost: "warn",
  localization_gained: "neutral",
  ws_lost: "warn",
  ws_connected: "neutral",
  lock_engaged: "neutral",
  lock_disengaged: "neutral",
  edge_boot: "neutral",
  edge_shutdown: "warn",
  map_switch: "neutral",
  error: "warn",
};

export function isTimelineEvent(kind: string): boolean {
  return kind in EVENT_TONE;
}

/**
 * Kit events + per-aisle milestones, merged into one ordered list.
 *
 * The `__none__` bucket is dropped: it collects locations with no aisle at all
 * (docks, manual nodes), and "corredor __none__ concluído" is noise, not a
 * milestone. An aisle with readings but no timestamps contributes nothing —
 * see `AisleCoverage.firstScanMs`.
 */
export function buildTimeline(
  events: readonly KitEvent[],
  aisles: readonly AisleCoverage[],
): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  for (const e of events) {
    if (!isTimelineEvent(e.kind)) continue;
    out.push({ ts: e.ts, type: "event", label: e.kind, tone: EVENT_TONE[e.kind] });
  }
  for (const a of aisles) {
    if (a.aisle === "__none__") continue;
    if (a.firstScanMs != null)
      out.push({ ts: a.firstScanMs, type: "aisle_start", label: a.aisle, tone: "neutral" });
    if (a.lastScanMs != null)
      out.push({ ts: a.lastScanMs, type: "aisle_done", label: a.aisle, tone: "good" });
  }
  // Stable by time; at the same instant an aisle's start reads before its end.
  const rank: Record<TimelineKind, number> = { event: 0, aisle_start: 1, aisle_done: 2 };
  return out.sort((l, r) => l.ts - r.ts || rank[l.type] - rank[r.type] || l.label.localeCompare(r.label));
}

/**
 * Whether the timeline needs dates, not just clock times.
 *
 * Both halves now name real instants (ADR 265), but they are still stamped by two
 * *devices*: the aisle milestones by whatever wrote items.csv, the kit events by the
 * edge as it recorded. A kit without NTP puts them days apart (the fixture kit is a
 * month out), and "08:00:00" over "00:31:19" then reads as an ordering bug rather than
 * as a clock that drifted. Showing the date makes the divergence visible instead of
 * confusing.
 */
export function spansMoreThanADay(entries: readonly TimelineEntry[]): boolean {
  if (entries.length < 2) return false;
  return entries[entries.length - 1].ts - entries[0].ts > 24 * 60 * 60 * 1000;
}
